// Batches and groups (plan v2 §4.3). A batch holds the people a classification workflow put there, each with the
// date they came in. A group joins batches: any of (or), all of (and), minus. The daily cut closes today's slice of a
// batch at its cut time (default 09:30): everyone who came in since the last cut. A scheduled campaign runs on it.
import { z } from 'zod'
import { Id, IsoDate, IsoDateTime, LeadCode } from './common'

const count = z.number().int().min(0)
export const CUT_TIME_DEFAULT = '09:30'
const CutTime = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'A time like 09:30')

export const BatchCut = z.object({
  id: Id,
  batch_id: Id,
  date: IsoDate,
  at: IsoDateTime,
  count: count.describe('the new people in this slice'),
  label: z.string().describe('e.g. "Metro salaried · 2026-10-26 · 1,240 new"'),
})
export type BatchCut = z.infer<typeof BatchCut>

export const Batch = z.object({
  id: Id,
  name: z.string(),
  cut_time: CutTime,
  people: count,
  new_since_cut: count.describe('came in since the last cut: the next slice'),
  last_cut: BatchCut.nullable(),
  filled_by: z.array(z.string()).describe('the classification workflows that put people here'),
})
export type Batch = z.infer<typeof Batch>

export const BatchMember = z.object({
  lead_code: LeadCode,
  added: IsoDate,
  cut_date: IsoDate.nullable().describe('the slice the person went out in; null = the next one'),
})
export type BatchMember = z.infer<typeof BatchMember>

export const MEMBERS_PAGE_SIZE = 100

export const BatchDetail = Batch.extend({
  members: z.array(BatchMember).describe('one page, newest first'),
  members_total: count,
  page: z.number().int().min(1),
  page_size: z.number().int().min(1),
  cuts: z.array(BatchCut).describe('newest first'),
})
export type BatchDetail = z.infer<typeof BatchDetail>

export const CreateBatchInput = z.object({
  name: z.string().trim().min(3, 'Give the batch a name'),
  cut_time: CutTime,
})
export type CreateBatchInput = z.infer<typeof CreateBatchInput>

export const GroupRule = z
  .object({
    any_of: z.array(Id).describe('in at least one of these batches'),
    all_of: z.array(Id).describe('and in every one of these'),
    minus: z.array(Id).describe('and in none of these'),
  })
  .refine((rule) => rule.any_of.length + rule.all_of.length > 0, {
    path: ['any_of'],
    message: 'Pick at least one batch under "any of" or "all of"',
  })
export type GroupRule = z.infer<typeof GroupRule>

export const GroupCount = z.object({
  people: count,
  today_cut: count.describe("people in the group from today's cuts of its batches"),
})
export type GroupCount = z.infer<typeof GroupCount>

export const BatchGroup = GroupCount.extend({
  id: Id,
  name: z.string(),
  rule: GroupRule,
  rule_words: z.string().describe('e.g. "Metro salaried or Hot leads, minus Non-metro"'),
})
export type BatchGroup = z.infer<typeof BatchGroup>

export const CreateGroupInput = z.object({
  name: z.string().trim().min(3, 'Give the group a name'),
  rule: GroupRule,
})
export type CreateGroupInput = z.infer<typeof CreateGroupInput>

export interface BatchesApi {
  listBatches(): Promise<Batch[]>
  /** page 1 = the newest MEMBERS_PAGE_SIZE members */
  getBatch(id: string, page?: number): Promise<BatchDetail>
  createBatch(input: CreateBatchInput): Promise<Batch>
  listGroups(): Promise<BatchGroup[]>
  createGroup(input: CreateGroupInput): Promise<BatchGroup>
  /** the live count while a group is being made */
  countGroup(rule: GroupRule): Promise<GroupCount>
}
